import styled from 'styled-components';

// ─── Page layout ─────────────────────────────────────────────────────────────

export const PageContainer = styled.div`
  padding: 1rem;
  width: 100%;
  max-width: 100%;
`;

export const PageHeaderBar = styled.div`
  background: var(--green-a60);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: var(--white);
  padding: 14px 18px;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  box-shadow: 0 4px 12px var(--shadow-a15);
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-family: var(--font-heading, sans-serif);
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.04em;
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

// ─── Form card ───────────────────────────────────────────────────────────────

export const FormCard = styled.section`
  background: var(--white-a55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--shadow-a05);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 1rem;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: 12px 16px;
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;

export const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const FieldLabel = styled.label`
  font-size: 0.74rem;
  font-weight: 600;
  color: var(--ink-a70);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const FormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

export const RadioGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

export const RadioCard = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--white-a55);
  border: 1px solid ${({ $active }) => ($active ? 'var(--orange-500)' : 'var(--shadow-a08)')};
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease;

  input { accent-color: var(--green-800); }
  &:hover { border-color: var(--orange-500); }
`;

export const RadioMain = styled.span`
  font-weight: 700;
  color: var(--black);
  font-size: 0.92rem;
`;

export const RadioSub = styled.span`
  font-size: 0.74rem;
  color: var(--ink-a60);
`;

// ─── Results area ────────────────────────────────────────────────────────────

export const ResultsArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const GroupBlock = styled.section`
  background: var(--white-a25);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid var(--shadow-a05);
  border-radius: 12px;
  overflow: hidden;
`;

export const GroupHeader = styled.header`
  background: var(--color-brand);
  color: var(--white);
  padding: 10px 16px;
  font-family: var(--font-heading, sans-serif);
  font-weight: 700;
  font-size: 0.92rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

export const GroupMeta = styled.span`
  font-size: 0.78rem;
  font-weight: 500;
  opacity: 0.85;
`;

export const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-body);

  th {
    background: var(--green-a08);
    color: var(--black);
    text-align: left;
    padding: 8px 12px;
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
    border-bottom: 1px solid var(--shadow-a08);
  }
  td {
    padding: 8px 12px;
    border-bottom: 1px solid var(--shadow-a05);
    font-size: 0.9rem;
  }
  td.numeric, th.numeric { text-align: right; }
`;

export const ClickableRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.15s ease;
  &:hover { background: var(--orange-a12); }
`;

export const EmptyRow = styled.div`
  padding: 16px 18px;
  text-align: center;
  color: var(--ink-a60);
  font-size: 0.9rem;
  font-style: italic;
`;

// ─── Modal ───────────────────────────────────────────────────────────────────

export const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: var(--shadow-a45);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

export const ModalPanel = styled.div`
  width: min(720px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  background: var(--white);
  border-radius: 16px;
  box-shadow: 0 20px 50px var(--shadow-a25);
  display: flex;
  flex-direction: column;
  padding: 18px;
  gap: 14px;
`;

export const ModalHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--shadow-a08);
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: var(--font-heading, sans-serif);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--green-800);
`;

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: 0;
  background: var(--shadow-a05);
  color: var(--black);
  border-radius: 50%;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: var(--shadow-a15); }
`;

export const ModalSection = styled.section`
  background: var(--green-a08);
  border: 1px solid var(--green-a08);
  border-radius: 12px;
  padding: 12px 14px;
`;

export const ModalSectionTitle = styled.h3`
  margin: 0 0 10px 0;
  font-family: var(--font-heading, sans-serif);
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.10em;
  color: var(--orange-500);
  font-weight: 700;
`;

export const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 16px;
`;

export const ModalField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const ModalLabel = styled.span`
  font-size: 0.70rem;
  color: var(--ink-a60);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

export const ModalValue = styled.span`
  font-family: var(--font-body);
  color: var(--black);
  font-size: 0.92rem;
`;

export const NegativeValue = styled.span`
  color: var(--red-600);
  font-weight: 600;
`;

export const ChartWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-top: 6px;
`;

export const TotalAdjLabel = styled.div`
  font-size: 0.85rem;
  color: var(--ink-a70);
  font-weight: 600;

  span { color: var(--green-800); font-weight: 700; margin-left: 6px; }
`;
